import { z } from 'zod';
import { IconType } from 'react-icons';
import type { GraphEdge, GraphNode } from './elements';
import type { LOGIC_BLOCKS_RULESET, LOGIC_CATEGORIES } from '../logic';
import { type OptionType, type Port, PortDirection } from '@morph-mapper/types';
import { Input } from '@morph-mapper/node-inputs';

export type LogicBlockRules = {
  configurable: boolean;
  multipleChildren: boolean;
};

export type OptionConditions = Partial<{
  // this record can be more strictly typed by options
  hasValue: Record<string, any>;
  isConnector: boolean;
  isFullWidth: boolean;
  isStatic: boolean;
  isHidden: boolean;
}>;

export type OptionConfig = {
  type: z.ZodType<any>;
  title: string;
  description: string;
  input: Input;
  selection?: {
    label: string;
    value: any;
  }[];
  default: any;
  conditions: OptionConditions;
  allowedOptionTypes?: OptionType[];
};

export type OptionsRecord = Record<string, OptionConfig>;

export type OptionsAsArgument<T extends OptionsRecord> = {
  [Property in keyof T]: z.infer<T[Property]['type']>;
};

export type PortConfig = {
  [PortDirection.Input]: readonly Port[];
  [PortDirection.Output]: readonly Port[];
};

export type InputPortIds<P extends PortConfig> =
  P[PortDirection.Input][number]['id'];

// For rules we only check the positive case.
export type BlockRules = {
  configurable: boolean;
  multipleChildren: boolean;
  singleChild: boolean;
  noChildren: boolean;
  multipleElement: boolean;
};

export enum BlockRule {
  Configurable = 'configurable',
  MultipleChildren = 'multipleChildren',
  SingleChild = 'singleChild',
  NoChildren = 'noChildren',
  MultipleElement = 'multipleElement',
}

/**
 * A generic, reusable shape for defining a logic block.
 * It specifies all required metadata (title, description, category, icon),
 * behavior (mutation, rules), and structure (options, ports, node type),
 * while keeping the concrete types of type (K), rules (R), options (D), and ports (P)
 * flexible so different block systems can plug in their own types.
 */
export type LogicBlockConfig<
  K extends string,
  R,
  D extends OptionsRecord,
  P extends PortConfig = PortConfig,
> = {
  title: string;
  category: keyof typeof LOGIC_CATEGORIES | 'internal';
  type: K;
  description: string;
  node: GraphNode;
  icon: IconType;
  options: D;
  ports: P;
  mutation: MutationWithInputs<P, OptionsAsArgument<D>>;
  rules: R;
};

/**
 * A concrete alias of LogicBlockConfig for standard block definition.
 * It fixes the generics, yielding the canonical fully-typed “block”.
 */
export type LogicBlock = LogicBlockConfig<
  LogicBlockIndex,
  Set<BlockRule>,
  OptionsRecord
>;

export type MutationContext = {
  /*
   * Determines if the current node is the root node of the logic tree.
   * This is used in cases where depending on the location of the node in the tree
   * we want to prevent rendering of certain elements,
   * for example when rendering may lead to duplication of the associated entry key.
   */
  isRoot: boolean;
};

export type MutationWithInputs<P extends PortConfig, T> = (args: {
  options: T;
  inputs: Record<InputPortIds<P>, unknown>;
  ctx: MutationContext;
  optionTypes: Record<string, OptionType>;
}) => any;

export type LogicBlockRecord<T extends Record<keyof T, any>, R> = {
  [K in keyof T]: LogicBlockConfig<K & string, R, T[K]>;
};

export const block = <
  K extends string,
  R,
  D extends OptionsRecord,
  const P extends PortConfig,
>(
  block: LogicBlockConfig<K, R, D, P>,
) => block;

export const typeLogicBlockRecord = <T extends Record<keyof T, any>>(
  map: LogicBlockRecord<T, BlockRule[]>,
) => map;

export type LogicBlockIndex =
  keyof (typeof LOGIC_BLOCKS_RULESET)[keyof typeof LOGIC_BLOCKS_RULESET];
export type OptionConfigKey<T extends LogicBlockIndex> =
  keyof (typeof LOGIC_BLOCKS_RULESET)[keyof typeof LOGIC_BLOCKS_RULESET][T]['options'];

type LogicConnectorConfig = {
  edge: GraphEdge;
};

type ConnectorRecord<T extends Record<keyof T, any>> = {
  [K in keyof T]: LogicConnectorConfig;
};

export const typeConnectorRecord = <
  T extends Record<keyof T, LogicConnectorConfig>,
>(
  map: ConnectorRecord<T>,
) => map;
